import { TrendingUp, TrendingDown, Trophy, ThumbsUp, Minus, CircleAlert } from 'lucide-react'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'

export const catColorMap: Record<string, { bg: string, text: string, hex: string, label: string, icon: React.ReactNode }> = {
  'A+': { bg: 'bg-emerald-500', text: 'text-white', hex: '#10b981', label: 'Excepcional', icon: <Trophy className="h-3 w-3" /> },
  'A':  { bg: 'bg-sky-500', text: 'text-white', hex: '#0ea5e9', label: 'Excelente', icon: <ThumbsUp className="h-3 w-3" /> },
  'B':  { bg: 'bg-amber-400', text: 'text-white', hex: '#fbbf24', label: 'Bueno', icon: <Minus className="h-3 w-3" /> },
  'C':  { bg: 'bg-rose-500', text: 'text-white', hex: '#f43f5e', label: 'Por Mejorar', icon: <CircleAlert className="h-3 w-3" /> },
}

export function SuperMetricCard({ icon, label, percentage, count, benchmark, category }: {
  icon: React.ReactNode
  label: string
  percentage?: number
  count: number
  benchmark?: number
  category?: string | null
}) {
  const diff = percentage !== undefined && benchmark !== undefined ? percentage - benchmark : null
  const isAbove = diff !== null && diff >= 0
  const formatNum = (n: number) => new Intl.NumberFormat('es-EC').format(n)
  const catKey = category?.toUpperCase() || null
  const colors = catKey ? (catColorMap[catKey] || catColorMap['C']) : null
  const gradientHex = colors?.hex || '#858796'

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      style={{
        background: `linear-gradient(to right, ${gradientHex}20, transparent 60%)`,
      }}
    >
      <div className="px-4 py-3">
        {/* Título arriba */}
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-2">{label}</p>

        <div className="flex items-center gap-3">
          {/* Icono grande en gris */}
          <div className="shrink-0 text-muted-foreground">
            {icon}
          </div>

          {/* Datos alineados a la derecha */}
          <div className="flex-1 min-w-0 text-right">
            {/* Porcentaje grande */}
            {percentage !== undefined ? (
              <div className="text-2xl font-bold text-foreground leading-tight">
                {percentage.toFixed(2)}%
              </div>
            ) : (
              <div className="text-2xl font-bold text-foreground leading-tight">
                {formatNum(count)}
              </div>
            )}
            {/* Variación vs benchmark */}
            {diff !== null && colors && (
              <div className="flex items-center gap-1 mt-0.5 justify-end">
                {isAbove ? (
                  <TrendingUp className="h-3 w-3" style={{ color: colors.hex }} />
                ) : (
                  <TrendingDown className="h-3 w-3" style={{ color: colors.hex }} />
                )}
                <span className="text-xs font-medium" style={{ color: colors.hex }}>
                  {isAbove ? '+' : ''}{diff.toFixed(2)}% vs {benchmark}%
                </span>
              </div>
            )}
            {/* Cantidad absoluta */}
            {percentage !== undefined && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatNum(count)} {label.toLowerCase()}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function GaugeChart({ value, benchmark, label, category, icon }: {
  value: number
  benchmark: number
  label: string
  category: string | null
  icon: React.ReactNode
}) {
  const catKey = category?.toUpperCase() || 'C'
  const colors = catColorMap[catKey] || catColorMap['C']
  const diff = value - benchmark
  const isAbove = diff >= 0

  // SVG semicircle gauge
  const radius = 80
  const strokeWidth = 16
  const circumference = Math.PI * radius // half circle
  // Clamp percentage to 0-100 for the arc display
  const displayPct = Math.min(Math.max(value, 0), 100)
  const offset = circumference - (displayPct / 100) * circumference

  return (
    <Card>
      <CardContent className="pt-6 flex flex-col items-center">
        <svg viewBox="0 0 200 120" className="w-full max-w-[200px]">
          {/* Background arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-300 dark:text-gray-600"
            strokeLinecap="round"
          />
          {/* Colored arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke={colors.hex}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-700"
          />
          {/* Center text */}
          <text x="100" y="85" textAnchor="middle" className="fill-foreground text-2xl font-bold" fontSize="28">
            {value.toFixed(1)}%
          </text>
        </svg>

        <div className="flex items-center gap-2 mt-2">
          {icon}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className="text-xs text-muted-foreground mt-1">
          Benchmark: &gt;{benchmark}%
        </span>

        <div className="flex items-center gap-1 mt-2">
          {isAbove ? (
            <TrendingUp className="h-3.5 w-3.5 text-green-600" />
          ) : (
            <TrendingDown className="h-3.5 w-3.5 text-red-600" />
          )}
          <span className={`text-xs font-medium ${isAbove ? 'text-green-600' : 'text-red-600'}`}>
            {isAbove ? '+' : ''}{diff.toFixed(2)}%
          </span>
          {category && (
            <span className={`ml-1 inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold ${colors.bg} ${colors.text}`}>
              {category}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function BenchmarkBarChart({ tasa, benchmark, label, category, icon }: {
  tasa: number
  benchmark: number
  label: string
  category: string | null
  icon: React.ReactNode
}) {
  const catKey = category?.toUpperCase() || 'C'
  const colors = catColorMap[catKey] || catColorMap['C']

  // Legacy logic: la barra mayor siempre al 100%, la menor proporcional
  let altBenchmark: number
  let altActual: number
  if (benchmark > tasa) {
    altBenchmark = 100
    altActual = benchmark > 0 ? (tasa / benchmark) * 100 : 0
  } else {
    altActual = 100
    altBenchmark = tasa > 0 ? (benchmark / tasa) * 100 : 0
  }

  return (
    <div className="flex flex-col items-center">
      <div className="flex flex-col items-center gap-1 mb-3">
        <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${colors.bg} ${colors.text}`}>
          {category || '-'}
        </span>
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-medium">{label}</span>
        </div>
      </div>

      {/* Bars container */}
      <div className="flex justify-center gap-5 w-full">
        {/* Benchmark bar */}
        <div className="flex flex-col items-center w-14">
          <span className="text-xs font-medium text-muted-foreground mb-1">{benchmark}%</span>
          <div className="w-full rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 relative" style={{ height: '160px' }}>
            <div
              className="absolute bottom-0 left-0 right-0 bg-gray-400 dark:bg-gray-500 rounded-b-md"
              style={{ height: `${altBenchmark}%`, borderRadius: altBenchmark === 100 ? '6px' : '0 0 6px 6px' }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground mt-1">Benchmark</span>
        </div>
        {/* Campaign bar */}
        <div className="flex flex-col items-center w-14">
          <span className="text-xs font-bold mb-1" style={{ color: colors.hex }}>{tasa.toFixed(1)}%</span>
          <div className="w-full rounded-md bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 relative" style={{ height: '160px' }}>
            <div
              className="absolute bottom-0 left-0 right-0 rounded-b-md"
              style={{
                height: `${altActual}%`,
                backgroundColor: colors.hex,
                borderRadius: altActual === 100 ? '6px' : '0 0 6px 6px',
              }}
            />
          </div>
          <span className="text-[10px] text-muted-foreground mt-1">Campaña</span>
        </div>
      </div>

      <Badge className={`mt-2 ${colors.bg} ${colors.text} text-[10px] gap-1`}>
        {colors.icon}
        {colors.label}
      </Badge>
    </div>
  )
}
